import { useContext, useEffect, useMemo } from "react";

import { toast } from "@/components/ui/toast";
import { UserContext, usePitchesByManagerIdThisMonth, useRecentPitchesByManagerId, useRelevantDocs } from "../../services/firebase";
import type { Campaign, Talent } from "../../shared/types";
import type { MyPitchRow } from "./rows";

/** A manager’s table page of pitches, joined to talent and campaign, plus this-month stats pitches. */
export function useMyPitches() {
    const user = useContext(UserContext);
    const isGrailTeam = Boolean(user?.roles?.manager || user?.roles?.admin || user?.roles?.externalManager || user?.roles?.superAdmin || user?.roles?.salesTeam || user?.roles?.brandTeam);
    const managerId = isGrailTeam ? user?.id : undefined;
    const monthQuery = usePitchesByManagerIdThisMonth(managerId);
    const tableQuery = useRecentPitchesByManagerId(managerId);

    useEffect(() => {
        if (!tableQuery.error) {
            return;
        }
        toast.add({
            type: "error",
            title: "Couldn’t load your pitches",
            description: "Refresh and try again.",
        });
        console.error(tableQuery.error);
    }, [tableQuery.error]);

    useEffect(() => {
        if (!monthQuery.error) {
            return;
        }
        toast.add({
            type: "error",
            title: "Couldn’t load your pitches",
            description: "Refresh and try again.",
        });
        console.error(monthQuery.error);
    }, [monthQuery.error]);

    const talentIds = useMemo(() => {
        const ids = tableQuery.pitches.map((pitch) => pitch.talentId).filter(Boolean);
        return [...new Set(ids)].sort();
    }, [tableQuery.pitches]);
    const campaignIds = useMemo(() => {
        const ids = tableQuery.pitches.map((pitch) => pitch.campaignId).filter(Boolean);
        return [...new Set(ids)].sort();
    }, [tableQuery.pitches]);
    const { data: talentData } = useRelevantDocs<Talent>({
        collectionName: "talent",
        docIds: talentIds,
        enabled: talentIds.length > 0,
    });
    const { data: campaigns } = useRelevantDocs<Campaign>({
        collectionName: "campaigns",
        docIds: campaignIds,
        enabled: campaignIds.length > 0,
    });

    const rows = useMemo(() => {
        const talentById = new Map((talentData ?? []).map((talent) => [talent.id, talent]));
        const campaignById = new Map((campaigns ?? []).map((campaign) => [campaign.id, campaign]));
        const joined: MyPitchRow[] = tableQuery.pitches.map((pitch) => ({
            ...pitch,
            talent: talentById.get(pitch.talentId),
            campaign: campaignById.get(pitch.campaignId),
        }));
        return joined;
    }, [campaigns, tableQuery.pitches, talentData]);

    return {
        user,
        isGrailTeam,
        loading: tableQuery.loading,
        monthLoading: monthQuery.loading,
        rows,
        pitches: monthQuery.pitches,
    };
}
